import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaBell, FaExclamationTriangle, FaInfoCircle, FaCheckCircle } from 'react-icons/fa';
import '../styles/NotificationCenter.css';
import { getCurrentUserId, getCurrentUserRole } from '../utils/auth';
import { getApiUrl } from '../utils/config.js';
import LoadingSpinner from './LoadingSpinner';

const NotificationCenter = ({ userType, userId: propUserId }) => {
    const [notifications, setNotifications] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const eventSourceRef = useRef(null);
    const dropdownRef = useRef(null);
    const reconnectTimeoutRef = useRef(null); // Track reconnection timeout
    const isMountedRef = useRef(true); // Track if component is mounted
    // Every notification id this tab has ever displayed — a live SSE stream can
    // legitimately redeliver the same id (a reconnect that overlaps the previous
    // connection, a server-side resend), and this is the single source of truth
    // that keeps a redelivery from re-counting as unread or popping a second
    // native browser notification. Seeded from the initial REST fetch so a
    // notification loaded on mount never double-fires if SSE reports it again.
    const seenNotificationIdsRef = useRef(new Set());
    
    // Get user ID from props or auth utils
    const userId = propUserId || getCurrentUserId();
    const currentUserType = userType || getCurrentUserRole();

    // Helper functions for notification formatting
    const getPriorityIcon = (priority) => {
        switch (priority) {
            case 'high': return <FaExclamationTriangle className="priority-icon high" style={{ color: '#ef4444' }} />;
            case 'medium': return <FaInfoCircle className="priority-icon medium" style={{ color: '#f59e0b' }} />;
            case 'low': return <FaCheckCircle className="priority-icon low" style={{ color: '#10b981' }} />;
            default: return <FaInfoCircle className="priority-icon medium" style={{ color: '#f59e0b' }} />;
        }
    };

    const getNotificationTitle = (type) => {
        const titles = {
            'evaluation': 'Evaluation Required',
            'attendance': 'Attendance Update',
            'attendance_request': 'Attendance Request',
            'schedule': 'Schedule Update',
            'assignment': 'New Assignment',
            'reminder': 'Reminder',
            'system': 'System Notification',
            'general': 'General Notification'
        };
        return titles[type] || 'Notification';
    };

    const getNotificationMessage = (notification) => {
        const { notificationType, isActionBased, actionStatus, eventId } = notification;
        
        const messages = {
            'evaluation': isActionBased 
                ? (actionStatus ? 'Evaluation completed successfully' : 'Please complete your evaluation')
                : 'New evaluation available',
            'attendance': 'Attendance record updated',
            'attendance_request': isActionBased 
                ? (actionStatus ? 'Attendance request approved' : 'Attendance request pending approval')
                : 'New attendance request received',
            'schedule': 'Your schedule has been updated',
            'assignment': 'New assignment has been posted',
            'reminder': 'You have pending tasks',
            'system': 'System maintenance notification',
            'general': 'You have a new notification'
        };
        
        let message = messages[notificationType] || 'You have a new notification';
        
        if (eventId) {
            message += ` (Event: ${eventId.slice(-8)})`;
        }
        
        return message;
    };

    const formatTime = (timestamp) => {
        if (!timestamp) return 'Just now';
        
        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);
        
        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        
        return date.toLocaleDateString();
    };

    const truncateText = (text, maxLength = 80) => {
        if (!text || text.length <= maxLength) return text;
        return text.substring(0, maxLength).trim() + '...';
    };

    // Load initial notifications
    const loadNotifications = useCallback(async () => {
        if (!userId) return;
        
        try {
            setIsLoading(true);
            const token = localStorage.getItem('access_token');
            const response = await fetch(
                getApiUrl(`notifications/user/${userId}?page=1&limit=20`),
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
            
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const data = await response.json();

            // Process notifications
            let processedNotifications = [];
            let unreadCount = 0;

            if (Array.isArray(data)) {
                processedNotifications = data;
                unreadCount = data.filter(n => !n.isViewed).length;
            } else if (data.notifications) {
                processedNotifications = data.notifications;
                unreadCount = data.unread_count || 0;
            }

            // Defensive de-dupe by id (map preserves first-seen order) in case the
            // backend ever returns the same document twice for one page.
            const byId = new Map();
            processedNotifications.forEach((notification) => {
                if (!byId.has(notification._id)) byId.set(notification._id, notification);
            });

            const formattedNotifications = Array.from(byId.values()).map(notification => ({
                id: notification._id,
                title: getNotificationTitle(notification.notificationType),
                message: notification.message || getNotificationMessage(notification),
                seen: notification.isViewed,
                created_at: notification.created_at,
                type: notification.notificationType,
                isActionBased: notification.isActionBased,
                actionStatus: notification.actionStatus,
                eventId: notification.eventId,
                user: notification.user
            }));

            // Seed the "already displayed" set so a live SSE redelivery of any of
            // these never re-triggers an unread-count bump or a browser popup.
            formattedNotifications.forEach((notification) => seenNotificationIdsRef.current.add(notification.id));

            setNotifications(formattedNotifications);
            setUnreadCount(unreadCount);
            setIsConnected(true);
            
        } catch (error) {
            console.error('Failed to load notifications:', error);
            setIsConnected(false);
        } finally {
            setIsLoading(false);
        }
    }, [userId]);

    // Setup SSE for live notifications
    const setupSSE = useCallback(() => {
        if (!userId || !isMountedRef.current) return;
        
        // Close existing connection and clear any pending reconnection
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
        }
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
        }
        
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                setIsConnected(false);
                return;
            }

            if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('🔗 Setting up live notifications for user:', userId);
            const eventSource = new EventSource(
                getApiUrl(`notifications/stream/${userId}?token=${encodeURIComponent(token)}`)
            );
            
            eventSourceRef.current = eventSource;
            
            eventSource.onopen = () => {
                if (!isMountedRef.current) return;
                if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('✅ Live notifications connected');
                setIsConnected(true);
            };
            
            eventSource.onmessage = (event) => {
                if (!isMountedRef.current) return;
                
                try {
                    const payload = JSON.parse(event.data);

                    // Skip non-notification stream events
                    if (payload.type === 'connection' || payload.type === 'heartbeat' || payload.type === 'error') {
                        return;
                    }

                    const rawNotification = payload.notification || payload;
                    if (!rawNotification || !rawNotification._id) {
                        return;
                    }
                    
                    if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('📨 New live notification:', rawNotification);

                    // A live stream can legitimately redeliver the same id — a
                    // reconnect that briefly overlaps the previous connection, or a
                    // server-side resend. Gate every side effect (list insert, unread
                    // count, native popup) on this single check so a redelivery is a
                    // total no-op instead of re-counting or re-popping.
                    if (seenNotificationIdsRef.current.has(rawNotification._id)) {
                        if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('↩️ Ignoring redelivered notification:', rawNotification._id);
                        return;
                    }
                    seenNotificationIdsRef.current.add(rawNotification._id);

                    // Process the notification
                    const notification = {
                        id: rawNotification._id,
                        title: getNotificationTitle(rawNotification.notificationType),
                        message: rawNotification.message || getNotificationMessage(rawNotification),
                        seen: rawNotification.isViewed || false,
                        created_at: rawNotification.created_at,
                        type: rawNotification.notificationType,
                        isActionBased: rawNotification.isActionBased,
                        actionStatus: rawNotification.actionStatus,
                        eventId: rawNotification.eventId,
                        user: rawNotification.user
                    };

                    if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('✨ Adding live notification:', notification.id);
                    setNotifications(prev => [notification, ...prev]);

                    // Increment unread count
                    if (!notification.seen) {
                        setUnreadCount(prev => prev + 1);
                    }

                    // Show browser notification
                    if (window.Notification && Notification.permission === 'granted') {
                        new Notification(notification.title, {
                            body: notification.message,
                            icon: '/favicon.ico'
                        });
                    }
                    
                } catch (error) {
                    console.error('Failed to parse live notification:', error);
                }
            };
            
            eventSource.onerror = (error) => {
                if (!isMountedRef.current) return;

                setIsConnected(false);
                const readyState = eventSource.readyState;
                
                const currentToken = localStorage.getItem('access_token');
                if (!currentToken) {
                    if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('🔌 No auth token found, user logged out - stopping SSE reconnection');
                    return;
                }

                if (readyState === EventSource.CONNECTING) {
                    if (import.meta.env.VITE_DEBUG_MODE === 'true') console.warn('SSE temporarily interrupted, browser is reconnecting...');
                    return;
                }

                if (import.meta.env.VITE_DEBUG_MODE === 'true') console.warn('SSE stream closed, forcing reconnect in 3 seconds...');
                eventSource.close();
                reconnectTimeoutRef.current = setTimeout(() => {
                    if (isMountedRef.current && localStorage.getItem('access_token')) {
                        setupSSE();
                    }
                }, 3000);
            };
            
        } catch (error) {
            console.error('Failed to setup SSE:', error);
            setIsConnected(false);
        }
    }, [userId]);

    // Mark notification as seen
    const markAsSeen = async (notificationId) => {
        try {
            const token = localStorage.getItem('access_token');
            await fetch(getApiUrl(`notifications/${notificationId}/read`), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            setNotifications(prev =>
                prev.map(notif =>
                    notif.id === notificationId ? { ...notif, seen: true } : notif
                )
            );
            setUnreadCount(prev => Math.max(0, prev - 1));
            
        } catch (error) {
            console.error('Failed to mark notification as seen:', error);
        }
    };

    // Initialize notifications
    useEffect(() => {
        if (!userId) {
            return;
        }

        isMountedRef.current = true;
        if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('🚀 Initializing notifications for user:', userId);
        
        loadNotifications();
        setupSSE();
        
        // Request notification permission
        if (window.Notification && Notification.permission === 'default') {
            Notification.requestPermission();
        }
        
        return () => {
            if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('🧹 NotificationCenter cleanup');
            isMountedRef.current = false;
            
            // Close SSE connection
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
                eventSourceRef.current = null;
            }
            
            // Clear any pending reconnection timeout
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
        };
    }, [userId, loadNotifications, setupSSE]);

    // Close on any outside pointer interaction, including navbar buttons and nested controls.
    useEffect(() => {
        if (!isOpen) return;

        const handlePointerDown = (event) => {
            const target = event.target;
            if (dropdownRef.current && target instanceof Node && !dropdownRef.current.contains(target)) {
                setIsOpen(false);
            }
        };

        const handleNavbarInteraction = () => {
            setIsOpen(false);
        };

        document.addEventListener('pointerdown', handlePointerDown, true);
        window.addEventListener('clinexpert-navbar-interaction', handleNavbarInteraction);
        return () => {
            document.removeEventListener('pointerdown', handlePointerDown, true);
            window.removeEventListener('clinexpert-navbar-interaction', handleNavbarInteraction);
        };
    }, [isOpen]);

    const toggleNotifications = () => {
        window.dispatchEvent(new CustomEvent('clinexpert-navbar-interaction'));
        setIsOpen(!isOpen);
    };

    return (
        <div className="notification-center-wrapper" ref={dropdownRef}>
            <button 
                className={`notification-bell-button ${!isConnected ? 'disconnected' : ''} ${unreadCount > 0 ? 'has-notifications' : ''}`}
                onClick={toggleNotifications}
                aria-label="Notifications"
                title={isConnected ? 'Live Notifications (Connected)' : 'Notifications (Reconnecting...)'}
            >
                <div className="bell-icon-wrapper">
                    <FaBell className="bell-icon" />
                    {!isConnected && (
                        <span className="connection-warning">!</span>
                    )}
                    {unreadCount > 0 && (
                        <span className="notification-count-badge">
                            {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                    )}
                </div>
            </button>

            {isOpen && (
                <div className="notification-dropdown-panel">
                    <div className="notification-header-section">
                        <div className="header-gradient"></div>
                        <div className="header-content">
                            <h3 className="notifications-title">Notification Center</h3>
                            <div className="header-info">
                                {unreadCount > 0 && (
                                    <span className="unread-counter">{unreadCount} unread</span>
                                )}
                                <span className={`live-status ${isConnected ? 'connected' : 'disconnected'}`}>
                                    <span className="status-dot"></span>
                                    {isConnected ? 'Live' : 'Reconnecting...'}
                                </span>
                            </div>
                        </div>
                    </div>
                    
                    <div className="notification-body-section">
                        {isLoading && notifications.length === 0 ? (
                            <div className="notification-loading-state">
                                <LoadingSpinner text="Loading notifications..." variant="inline" size="small" />
                            </div>
                        ) : notifications.length === 0 ? (
                            <div className="empty-notifications-state">
                                <div className="empty-icon-wrapper">
                                    <FaBell className="empty-bell-icon" />
                                    <div className="empty-shine"></div>
                                </div>
                                <h4 className="empty-title">All caught up!</h4>
                                <p className="empty-subtitle">No notifications at this time</p>
                                <p className="empty-subtitle">Live notifications enabled</p>
                            </div>
                        ) : (
                            <div className="notifications-list-container">
                                {notifications.map((notification) => {
                                    const isAnnouncement = notification.type === 'announcement';
                                    
                                    return (
                                        <div 
                                            key={notification.id} 
                                            className={`notification-card ${!notification.seen ? 'unread-card' : 'read-card'} ${notification.type}-type`}
                                            onClick={() => {
                                                if (!notification.seen) {
                                                    markAsSeen(notification.id);
                                                }
                                            }}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <div className="card-content">
                                                {!notification.seen && <div className="unread-indicator-dot"></div>}
                                                
                                                {isAnnouncement ? (
                                                    // Announcement layout with priority icon
                                                    <div className="notification-main-content announcement-content">
                                                        <div className="announcement-header">
                                                            <div className="priority-and-title">
                                                                {getPriorityIcon(notification.priority || 'medium')}
                                                                <h4 className="notification-card-title announcement-title">
                                                                    {notification.title}
                                                                </h4>
                                                            </div>
                                                        </div>
                                                        <div className="announcement-preview">
                                                            <p className="announcement-preview-text">
                                                                {truncateText(notification.message, 80)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    // Regular notification layout
                                                    <div className="notification-main-content">
                                                        <h4 className="notification-card-title">{notification.title}</h4>
                                                        <p className="notification-card-message">{notification.message}</p>
                                                    </div>
                                                )}
                                                
                                                <div className="notification-card-footer">
                                                    <span className="notification-timestamp">
                                                        {formatTime(notification.created_at)}
                                                    </span>
                                                    <span className={`type-badge ${notification.type}-badge`}>
                                                        {isAnnouncement ? 'Announcement' : notification.type}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationCenter;


